/**
 * Composable for managing order history
 * Updated to support API V2 with backward compatibility
 * Follows DRY principles and Vue.js Style Guide
 */

import { ref, computed } from 'vue'
import { consola } from 'consola'
import { useUserProfileStore , apiProfile } from '@entities/user'
import type { Order, APIPagination } from '@entities/order'


// Logger
const logger = consola.withTag('order-history')

// Configuration constants
const PAGINATION_CONFIG = {
  perPage: 10,
  defaultOffset: 0,
} as const

export const useOrderHistory = () => {
  const storeUserProfile = useUserProfileStore()

  // Reactive state
  const loading = ref(false)
  const loadingMore = ref(false)
  const orderList = ref<Order[]>([])
  const error = ref<string | null>(null)
  const hasMore = ref(true)
  const currentOffset = ref(PAGINATION_CONFIG.defaultOffset)
  const currentPage = ref(1)
  
  const paginationDef = ref<APIPagination>({
    total: 0,
    limit: PAGINATION_CONFIG.perPage,
    offset: PAGINATION_CONFIG.defaultOffset,
    hasMore: true,
    totalPages: 0,
    currentPage: 1,
  })

  // Computed properties
  const isEmpty = computed(() => orderList.value.length === 0)
  const isInitialLoading = computed(() => loading.value && orderList.value.length === 0)
  const canLoadMore = computed(() => hasMore.value && !loadingMore.value)
  
  // Computed pagination properties
  const totalPages = computed(() => Math.ceil(paginationDef.value.total / PAGINATION_CONFIG.perPage))
  
  // Update paginationDef with computed values
  const _updatePaginationDef = () => {
    paginationDef.value = {
      ...paginationDef.value,
      totalPages: totalPages.value,
      currentPage: currentPage.value,
    }
  }

  /**
   * Fetch orders from API
   */
  const fetchOrders = async (page: number = 1): Promise<void> => {
    if (loading.value) return

    if (process.dev) {
      logger.debug('fetchOrders called', {
        userId: storeUserProfile.getUserId,
        page,
      })
    }

    if (!storeUserProfile.getUserId) {
      logger.error('No userId available')
      error.value = 'Не удалось определить пользователя — войдите в аккаунт заново'
      return
    }

    try {
      loading.value = true
      error.value = null

      // Calculate offset from page
      const offset = (page - 1) * PAGINATION_CONFIG.perPage
      currentOffset.value = offset
      currentPage.value = page

      const response = await apiProfile.getOrderHistory({
        userId: String(storeUserProfile.getUserId),
        limit: PAGINATION_CONFIG.perPage,
        offset,
      })

      if (process.dev) {
        logger.debug('API Response received', {
          success: response.success,
          ordersCount: response.orders?.length || 0,
          pagination: response.pagination,
        })
      }

      if (response.success) {
        const { orders, pagination } = response

        orderList.value = orders || []
        hasMore.value = pagination.hasMore
        
        // Update pagination data
        paginationDef.value = {
          ...pagination,
          totalPages: Math.ceil(pagination.total / PAGINATION_CONFIG.perPage),
          currentPage: page,
        }
      } else {
        logger.warn('API returned success: false', response)
        error.value = 'Не удалось загрузить историю (сервер вернул ошибку)'
      }
    } catch (error_) {
      logger.error('Ошибка при загрузке заказов:', error_)
      error.value = (error_ as any)?.data?.message || (error_ as any)?.message || 'Ошибка загрузки истории заказов'
    } finally {
      loading.value = false
    }
  }

  /**
   * Load more orders (for mobile version)
   */
  const loadMore = async (): Promise<void> => {
    if (!hasMore.value || loadingMore.value) return

    try {
      loadingMore.value = true

      // Увеличиваем offset
      currentOffset.value += PAGINATION_CONFIG.perPage

      const response = await apiProfile.getOrderHistory({
        userId: String(storeUserProfile.getUserId),
        limit: PAGINATION_CONFIG.perPage,
        offset: currentOffset.value,
      })

      if (response.success) {
        const { orders, pagination } = response
        orderList.value = [...orderList.value, ...orders]
        hasMore.value = pagination.hasMore
        
        // Update pagination data
        paginationDef.value = {
          ...pagination,
          totalPages: Math.ceil(pagination.total / PAGINATION_CONFIG.perPage),
          currentPage: Math.floor(currentOffset.value / PAGINATION_CONFIG.perPage) + 1,
        }
      }
    } catch (error) {
      logger.error('Ошибка при загрузке дополнительных заказов:', error)
      // Rollback offset on error
      currentOffset.value -= PAGINATION_CONFIG.perPage
    } finally {
      loadingMore.value = false
    }
  }

  /**
   * Initialize order history
   */
  const initLoad = async (): Promise<void> => {
    if (process.dev) {
      logger.debug('initLoad called')
    }
    
    // Wait for user data to load
    if (!storeUserProfile.getUserId) {
      if (process.dev) {
        logger.debug('Waiting for user data...')
      }
      await storeUserProfile.fetchUser()
    }
    
    await fetchOrders()
  }

  /**
   * Reset to initial state
   */
  const reset = (): void => {
    orderList.value = []
    hasMore.value = true
    currentOffset.value = PAGINATION_CONFIG.defaultOffset
    currentPage.value = 1
    loading.value = false
    loadingMore.value = false
  }

  return {
    // State
    loading,
    loadingMore,
    orderList,
    error,
    hasMore,
    currentOffset,
    currentPage,
    paginationDef,
    
    // Computed
    isEmpty,
    isInitialLoading,
    canLoadMore,
    
    // Methods
    fetchOrders,
    loadMore,
    initLoad,
    reset,
  }
} 